import { useEffect, useState, useMemo } from 'react';

import { UploadCloud } from 'lucide-react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';

import { Confirmation } from './components/Confirmation';
import { SettingsLayout } from './components/SettingsLayout';
import { SettingsProvider } from './context/SettingsContext';
import { CaseDetailsRoute } from './routes/CaseDetailsRoute';
import { CaseMetadataRoute } from './routes/CaseMetadataRoute';
import { DeleteDocsRoute } from './routes/DeleteDocsRoute';
import { DocumentsRoute } from './routes/DocumentsRoute';
import { RunsRoute } from './routes/RunsRoute';
import { UploadsRoute } from './routes/UploadsRoute';
import { RunStatus } from '../../../../../../backend/src/types/types';
import { isProcessingStatus } from '../../../../constants/pipelineStatus';
import { useGetCase } from '@/api/queries/useGetCase';
import Modal from '@/components/molecules/Modals/SettingsModal';

const Settings = () => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  if (!caseId) {
    return <></>;
  }

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [totalUnprocessedDocs, setTotalUnprocessedDocs] = useState(0);

  // -------- Synopsis state at this level so that it will persist between tabs

  const [originalIssues, setOriginalIssues] = useState('');
  const [caseIssues, setCaseIssues] = useState('');

  const [originalParties, setOriginalParties] = useState('');
  const [caseParties, setCaseParties] = useState('');

  const [originalContext, setOriginalContext] = useState('');
  const [caseContext, setCaseContext] = useState('');

  const [originalTimePeriod, setOriginalTimePeriod] = useState('');
  const [caseTimePeriod, setCaseTimePeriod] = useState('');

  const [detailsChanged, setDetailsChanged] = useState(false);

  const { isLoading: isLoadingCaseIssues, data: caseObjectResp } = useGetCase(caseId);

  useEffect(() => {
    if (caseObjectResp) {
      setCaseIssues(caseObjectResp?.legalIssues || '');
      setOriginalIssues(caseObjectResp?.legalIssues || '');

      setCaseParties(caseObjectResp?.parties || '');
      setOriginalParties(caseObjectResp?.parties || '');

      setCaseContext(caseObjectResp?.keyContext || '');
      setOriginalContext(caseObjectResp?.keyContext || '');

      setCaseTimePeriod(caseObjectResp?.keyTimePeriod || '');
      setOriginalTimePeriod(caseObjectResp?.keyTimePeriod || '');
    }
  }, [caseObjectResp]);

  const handleIssuesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaseIssues(event.target.value);
  };
  const handlePartiesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCaseParties(event.target.value);
  };
  const handleContextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCaseContext(event.target.value);
  };
  const handleTimePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCaseTimePeriod(event.target.value);
  };

  useEffect(() => {
    // TODO: maybe ignore this check and just set details changed if any of the handlers are called
    const isIssuesChanged = caseIssues.trim() !== originalIssues.trim();
    const isPartiesChanged = caseParties.trim() !== originalParties.trim();
    const isContextChanged = caseContext.trim() !== originalContext.trim();
    const isTimePeriodChanged = caseTimePeriod.trim() !== originalTimePeriod.trim();
    setDetailsChanged(isIssuesChanged || isPartiesChanged || isContextChanged || isTimePeriodChanged);
  }, [
    caseIssues,
    originalIssues,
    caseParties,
    originalParties,
    caseContext,
    originalContext,
    caseTimePeriod,
    originalTimePeriod,
  ]);

  const runs = caseObjectResp?.runs ?? [];
  const isReprocessing = runs.some((run) => isProcessingStatus(run.status as RunStatus));

  const contextValue = useMemo(
    () => ({
      caseId,
      caseName: caseObjectResp?.name ?? '',
      createdDate: caseObjectResp?.createdDate,
      createdBy: caseObjectResp?.createdBy?.email ?? '',
      runs,
      isLoadingCaseIssues,
      originalIssues,
      caseIssues,
      setCaseIssues,
      originalParties,
      caseParties,
      setCaseParties,
      originalContext,
      caseContext,
      setCaseContext,
      originalTimePeriod,
      caseTimePeriod,
      setCaseTimePeriod,
      detailsChanged,
      setDetailsChanged,
      confirmationModalOpen,
      setConfirmationModalOpen,
      totalUnprocessedDocs,
      setTotalUnprocessedDocs,
      handleIssuesChange,
      handlePartiesChange,
      handleContextChange,
      handleTimePeriodChange,
      isReprocessing,
      openModal: setConfirmationModalOpen,
    }),
    [
      caseId,
      caseObjectResp?.name,
      caseObjectResp?.createdDate,
      caseObjectResp?.createdBy?.email,
      runs,
      isLoadingCaseIssues,
      originalIssues,
      caseIssues,
      setCaseIssues,
      originalParties,
      caseParties,
      setCaseParties,
      originalContext,
      caseContext,
      setCaseContext,
      originalTimePeriod,
      caseTimePeriod,
      setCaseTimePeriod,
      detailsChanged,
      setDetailsChanged,
      confirmationModalOpen,
      setConfirmationModalOpen,
      totalUnprocessedDocs,
      setTotalUnprocessedDocs,
      handleIssuesChange,
      handlePartiesChange,
      handleContextChange,
      handleTimePeriodChange,
      isReprocessing,
      setConfirmationModalOpen,
    ],
  );

  return (
    <SettingsProvider value={contextValue}>
      <SettingsLayout>
        <Routes>
          <Route path="metadata" element={<CaseMetadataRoute />} />
          <Route path="details" element={<CaseDetailsRoute />} />
          <Route path="documents" element={<DocumentsRoute />} />
          <Route path="runs" element={<RunsRoute />} />
          <Route path="uploads" element={<UploadsRoute />} />
          <Route path="delete" element={<DeleteDocsRoute />} />
          <Route path="*" element={<Navigate to="metadata" replace />} />
        </Routes>
      </SettingsLayout>
      <Modal
        content={
          <Confirmation
            detailsChanged={detailsChanged}
            totalUnprocessedDocs={totalUnprocessedDocs}
            caseId={caseId}
            caseIssues={caseIssues}
            caseParties={caseParties}
            caseContext={caseContext}
            caseTimePeriod={caseTimePeriod}
            closeModal={() => {
              setConfirmationModalOpen(false);
            }}
          />
        }
        title={
          <div className="mt-1 flex h-full items-center gap-2 text-base font-semibold">
            <UploadCloud className="h-4 w-4 text-gray-700" />
            Update Case
          </div>
        }
        isOpen={confirmationModalOpen}
        handleClose={() => setConfirmationModalOpen(false)}
        size="small"
      />
    </SettingsProvider>
  );
};

export default Settings;
